import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarClock, CalendarRange, Users, Bell, FlaskConical, Inbox, ChevronRight, MapPin } from "lucide-react";

const KPI = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-5 shadow-soft" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="mt-4 text-3xl font-display font-bold text-stone-900">{value}</div>
    <div className="text-xs uppercase tracking-wider text-stone-500 mt-1">{label}</div>
  </div>
);

// Group an array of events into one entry per (patient_id, date).
// We sort cycle_day numerically so the displayed cycle reflects the earliest day in the group.
const groupByPatientDate = (events) => {
  const groups = new Map();
  for (const e of events) {
    const key = `${e.patient_id}__${e.date}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        date: e.date,
        patient_id: e.patient_id,
        patient_name: e.patient_name || "Unknown patient",
        patient_mrn: e.patient_mrn || null,
        calendar_id: e.calendar_id,
        regimen_name: e.regimen_name || null,
        cycle_label: e.cycle_label || null,
        cycle_day: e.cycle_day ?? null,
        locations: new Set(),
        event_count: 0,
        primary_type: e.event_type,
      });
    }
    const g = groups.get(key);
    if (e.location) g.locations.add(e.location);
    g.event_count += 1;
    // keep the earliest cycle day in the group as representative
    if (e.cycle_day != null && (g.cycle_day == null || e.cycle_day < g.cycle_day)) {
      g.cycle_label = e.cycle_label || g.cycle_label;
      g.cycle_day = e.cycle_day;
    }
  }
  return Array.from(groups.values()).map((g) => ({ ...g, locations: Array.from(g.locations) }));
};

const PatientRow = ({ row, showDate, onClick }) => {
  const d = new Date(row.date);
  const initials = (row.patient_name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "P";
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 py-3 px-4 hover:bg-stone-50 rounded-lg border border-transparent hover:border-stone-200 transition-colors text-left"
      data-testid={`patient-row-${row.key}`}
    >
      {showDate ? (
        <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center text-xs font-bold leading-tight bg-brand-100 text-brand-700 shrink-0">
          <span className="text-[10px] uppercase">{d.toLocaleDateString(undefined, { month: "short" })}</span>
          <span className="text-sm font-display font-bold">{d.getDate()}</span>
        </div>
      ) : (
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-brand-100 text-brand-700 shrink-0">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-stone-900 truncate">
          {row.patient_name}
          {row.patient_mrn ? <span className="ml-2 text-xs font-normal text-stone-500">{row.patient_mrn}</span> : null}
        </div>
        <div className="text-xs text-stone-500 truncate flex items-center gap-1.5 mt-0.5">
          {row.cycle_label ? <span>{row.cycle_label} · D{row.cycle_day}</span> : null}
          {row.locations.length > 0 ? (
            <>
              {row.cycle_label ? <span className="text-stone-300">•</span> : null}
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{row.locations.join(", ")}</span>
            </>
          ) : null}
          {row.event_count > 1 ? (
            <>
              <span className="text-stone-300">•</span>
              <span>{row.event_count} events</span>
            </>
          ) : null}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
    </button>
  );
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  const todayRows = useMemo(
    () => (stats ? groupByPatientDate(stats.today_events).sort((a, b) => a.patient_name.localeCompare(b.patient_name)) : []),
    [stats]
  );
  const weekRows = useMemo(
    () =>
      stats
        ? groupByPatientDate(stats.week_events).sort((a, b) => (a.date === b.date ? a.patient_name.localeCompare(b.patient_name) : a.date.localeCompare(b.date)))
        : [],
    [stats]
  );

  if (!stats) return <div className="p-8 text-stone-500">Loading dashboard…</div>;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Dashboard</h1>
          <p className="text-stone-500 mt-1">Overview of today's treatment schedule and pending actions.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPI testId="kpi-today" icon={CalendarClock} label="Today's patients" value={todayRows.length} accent="bg-evt-drug-bg text-evt-drug-text" />
        <KPI testId="kpi-week" icon={CalendarRange} label="Patients this week" value={new Set(weekRows.map((r) => r.patient_id)).size} accent="bg-evt-clinic-bg text-evt-clinic-text" />
        <KPI testId="kpi-patients" icon={Users} label="Total patients" value={stats.total_patients} accent="bg-evt-rad-bg text-evt-rad-text" />
        <KPI testId="kpi-pending" icon={Bell} label="Pending approvals" value={(stats.pending_drug_requests || 0) + (stats.pending_reschedule_requests || 0)} accent="bg-evt-lab-bg text-evt-lab-text" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden" data-testid="today-list">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <h2 className="font-display font-semibold text-stone-900">Today</h2>
            <span className="text-xs text-stone-500">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
          </div>
          <div className="p-2 max-h-[420px] overflow-y-auto">
            {todayRows.length === 0 && <div className="p-6 text-center text-stone-500 text-sm">No patients scheduled today.</div>}
            {todayRows.map((row) => (
              <PatientRow key={row.key} row={row} showDate={false} onClick={() => nav(`/calendars/${row.calendar_id}`)} />
            ))}
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden" data-testid="week-list">
          <div className="px-5 py-4 border-b border-stone-100">
            <h2 className="font-display font-semibold text-stone-900">Coming up (next 7 days)</h2>
          </div>
          <div className="p-2 max-h-[420px] overflow-y-auto">
            {weekRows.length === 0 && <div className="p-6 text-center text-stone-500 text-sm">No upcoming patients.</div>}
            {weekRows.map((row) => (
              <PatientRow key={row.key} row={row} showDate={true} onClick={() => nav(`/calendars/${row.calendar_id}`)} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <button onClick={() => nav("/approvals")} className="bg-white border border-stone-200 rounded-xl p-5 hover:border-brand-300 hover:shadow-soft transition-all text-left" data-testid="approvals-shortcut">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warmth-100 text-warmth-500 flex items-center justify-center"><Inbox className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-semibold text-stone-900">Approval queue</div>
              <div className="text-sm text-stone-500">{stats.pending_drug_requests} drug request{stats.pending_drug_requests !== 1 ? "s" : ""}, {stats.pending_reschedule_requests} reschedule{stats.pending_reschedule_requests !== 1 ? "s" : ""}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-400" />
          </div>
        </button>
        <button onClick={() => nav("/regimens")} className="bg-white border border-stone-200 rounded-xl p-5 hover:border-brand-300 hover:shadow-soft transition-all text-left" data-testid="regimens-shortcut">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center"><FlaskConical className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-semibold text-stone-900">Regimens & calendars</div>
              <div className="text-sm text-stone-500">Browse standard regimens and create new patient calendars</div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-400" />
          </div>
        </button>
      </div>
    </div>
  );
}
